import { 
  Room, 
  RoomEvent, 
  LocalTrack, 
  RoomOptions,
  Track,
  ConnectionState,
  LocalAudioTrack
} from 'livekit-client';
import { APIEndpoints } from '../config/APIEndpoints';
import { LiveKitTokenService } from './LiveKitTokenService';

export type AudioCaptureMode = 'display' | 'microphone';

export class LiveKitService {
  private room: Room | null = null;
  private audioTrack: LocalTrack | null = null;
  private onStateChange: ((state: ConnectionState) => void) | null = null;
  private onError: ((error: Error) => void) | null = null;
  private captureMode: AudioCaptureMode = 'display';
  private isMicMuted: boolean = false;  // Default: mic enabled (not muted)

  private initializeRoom() {
    if (!this.room) {
      const roomOptions: RoomOptions = {
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          simulcast: false, // Disable simulcast for audio-only
          dtx: true, // Enable DTX for audio compression
        }
      };

      this.room = new Room(roomOptions);
      this.setupRoomListeners();
    }
  }

  constructor() {
    this.initializeRoom();
  }

  private setupRoomListeners() {
    if (!this.room) return;

    // Handle connection state changes
    this.room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      console.log('🔌 Connection state changed:', state);
      this.onStateChange?.(state);
    });

    // Handle errors
    this.room.on(RoomEvent.MediaDevicesError, (error: Error) => {
      console.error('❌ Media device error:', error);
      this.onError?.(error);
    });

    this.room.on(RoomEvent.Disconnected, () => {
      console.log('🔌 Disconnected from room');
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      console.log('🔄 Reconnecting to room...');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      console.log('✅ Reconnected to room');
    });

    // Handle track subscriptions
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('🎵 Track subscribed:', track.sid, 'from participant:', participant.identity);
      
      // Handle remote audio tracks (e.g., bot responses)
      if (track.kind === Track.Kind.Audio) {
        const audioEl = document.createElement('audio');
        audioEl.autoplay = true;
        track.attach(audioEl);
        document.body.appendChild(audioEl);
      }
    });

    // Cleanup when tracks are unsubscribed
    this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
    });
  }

  public setCallbacks(
    onStateChange: (state: ConnectionState) => void,
    onError: (error: Error) => void
  ) {
    this.onStateChange = onStateChange;
    this.onError = onError;
  }

  public async connect(identity: string, roomName?: string, mode: AudioCaptureMode = 'display'): Promise<string> {
    this.initializeRoom();
    this.captureMode = mode;

    try {
      // Get token from our backend (unique room will be generated if roomName is not provided)
      const tokenResponse = await LiveKitTokenService.getToken(identity, roomName);

      // Connect to LiveKit server with audio options
      if (!this.room) throw new Error('Room not initialized');
      await this.room.connect(APIEndpoints.LIVEKIT.SERVER_URL, tokenResponse.token, {
        autoSubscribe: true
      });
      console.log('✅ Connected to LiveKit room:', this.room?.name);

      // Start capturing audio based on mode
      await this.startAudioCapture();
      
      // Return the room name for WebSocket filtering
      return tokenResponse.room;
    } catch (error) {
      console.error('❌ Failed to connect to LiveKit room:', error);
      throw error;
    }
  }

  private async startAudioCapture(): Promise<void> {
    try {
      let stream: MediaStream;

      if (this.captureMode === 'microphone') {
        // Capture from user's microphone
        console.log('🎤 Starting microphone capture...');
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
            sampleRate: 48000
          },
          video: false
        });
        console.log('✅ Microphone access granted');
      } else {
        // Capture from system audio (tab audio) - display mode
        console.log('🖥️ Starting display media capture...');
        stream = await navigator.mediaDevices.getDisplayMedia({ 
          audio: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
            channelCount: 2,
            sampleRate: 48000
          },
          video: {
            width: 1,
            height: 1,
            frameRate: 1
          } // Minimal video to ensure audio capture works
        });
        console.log('✅ Display media access granted');
      }

      // Create LocalTrack from audio stream
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        this.audioTrack = new LocalAudioTrack(audioTrack);
      } else {
        throw new Error(`No audio track available from ${this.captureMode} audio`);
      }
      
      // Publish track if we're connected to a room
      if (this.room?.state === ConnectionState.Connected) {
        await this.room.localParticipant.publishTrack(this.audioTrack);
        console.log(`🎵 Started publishing ${this.captureMode} audio track`);
      }
    } catch (error) {
      console.error(`❌ Failed to start ${this.captureMode} audio capture:`, error);
      throw error;
    }
  }

  public async stopAudioCapture(): Promise<void> {
    if (this.audioTrack) {
      this.audioTrack.stop();
      if (this.room?.localParticipant) {
        await this.room.localParticipant.unpublishTrack(this.audioTrack);
      }
      this.audioTrack = null;
      console.log('🛑 Stopped audio capture');
    }
  }

  public disconnect(): void {
    this.stopAudioCapture();
    this.room?.disconnect();
    console.log('🔌 Disconnected from LiveKit service');
  }

  public isConnected(): boolean {
    return this.room?.state === ConnectionState.Connected;
  }

  public getConnectionState(): ConnectionState | null {
    return this.room?.state ?? null;
  }

  public toggleMicrophone(): boolean {
    if (!this.audioTrack) {
      console.warn('⚠️ Audio track not available');
      return false;
    }

    this.isMicMuted = !this.isMicMuted;
    
    if (this.isMicMuted) {
      // Mute: stop the track
      this.audioTrack.mute();
      console.log('🔇 Microphone muted');
    } else {
      // Unmute: unmute the track
      this.audioTrack.unmute();
      console.log('🔊 Microphone unmuted');
    }

    return this.isMicMuted;
  }

  public resetMicrophone(): void {
    this.isMicMuted = false;
    if (this.audioTrack) {
      this.audioTrack.unmute();
      console.log('🔊 Microphone reset to enabled');
    }
  }

  public isMicrophoneMuted(): boolean {
    return this.isMicMuted;
  }

  public getMicrophoneState(): 'on' | 'off' | 'unavailable' {
    if (!this.audioTrack) {
      return 'unavailable';
    }
    return this.isMicMuted ? 'off' : 'on';
  }
}
